import { Component } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';

@Component({
  selector: 'app-organization-management',
  templateUrl: './organization-management.component.html',
  styleUrl: './organization-management.component.scss',
  providers: [Location],
})
export class OrganizationManagementComponent {
  organizationCreated = false;
  selectedTabIndex = 0;
  tabRoutes = [
    '/organization/add-customer',
    '/organization/edit-organization',
    '/organization/add-sub-organization',
    '/organization/edit-sub-organization',
    '/organization/org-settings',
    '/organization/add-user',
    '/organization/show-user',
  ];

  orgTabs = [
    {
      label: 'Add Organization',
      icon: 'business',
      desc: 'Create a new customer',
      permission: 'AccessProductSupportConsole',
      route: 'add-customer',
    },
    {
      label: 'Show Organization',
      icon: 'edit',
      desc: 'Show and Update organization details',
      permission: 'AccessProductSupportConsole',
      route: 'show-organization',
    },
    {
      label: 'Add Sub Organization',
      icon: 'account_tree',
      desc: 'Add a location',
      permission: 'ViewPayers',
      route: 'add-sub-organization',
    },
    {
      label: 'Show Sub Organization(s)',
      icon: 'edit_location_alt',
      desc: 'Show and update sub-organization(s)',
      permission: 'ViewPayers',
      route: 'edit-sub-organization',
    },
    {
      label: 'Organization Settings',
      icon: 'settings',
      desc: 'Configure company settings',
      permission: 'ViewPayers',
      route: 'org-settings',
    },
    {
      label: 'Create User',
      icon: 'person_add',
      desc: 'add a user',
      permission: 'ViewPayers',
      route: 'add-user',
    },
    {
      label: 'Show User(s)',
      icon: 'people',
      desc: 'show and edit user(s)',
      permission: 'ViewPayers',
      route: 'show-user',
    },
  ];

  constructor(
    public authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly orgState: OrganizationStateService
  ) {
    // Set selectedTabIndex based on current route
    this.router.events.subscribe(() => {
      const currentUrl = this.router.url;
      if (currentUrl.includes('show-organization') || currentUrl.includes('edit-organization')) {
        this.selectedTabIndex = 1;
      } else if (currentUrl.includes('add-sub-organization')) {
        this.selectedTabIndex = 2;
      } else if (currentUrl.includes('edit-sub-organization')) {
        this.selectedTabIndex = 3;
      } else if (currentUrl.includes('org-settings')) {
        this.selectedTabIndex = 4;
      } else if (currentUrl.includes('add-user')) {
        this.selectedTabIndex = 5;
      } else if (
        currentUrl.includes('show-user') ||
        currentUrl.includes('edit-user')
      ) {
        this.selectedTabIndex = 6;
      } else {
        this.selectedTabIndex = 0;
      }
    });

    // Listen for org creation (update this logic as per your actual org creation event)
    this.orgState.orgResponse$.subscribe((org) => {
      this.organizationCreated = !!(org && (org.orgId || org.id));
    });
  }

  onTabChange(index: number) {
    // Disable all tabs except Add Organization until an org is created
    if (!this.organizationCreated && index !== 0) {
      return;
    }

    // Permission checks for each tab
    const selectedTab = this.orgTabs[index];
    if (
      !selectedTab ||
      !this.authService.hasPermission(selectedTab.permission)
    ) {
      return; // Block navigation if permission is missing
    }

    this.selectedTabIndex = index;
    this.navigateToTab(selectedTab, index);
  }

  // Helper for template: returns true if the tab should be blurred/disabled
  isTabBlurred(index: number): boolean {
    return !this.organizationCreated && index !== 0;
  }

  private navigateToTab(tab: any, index: number) {
    if (index === 0) {
      // Add Customer doesn't need orgId
      this.router.navigate([tab.route], { relativeTo: this.route });
    } else if (index === 1) {
      // Edit Organization needs orgId
      this.navigateWithOrgId(tab);
    } else if (index === 3) {
      // Edit Sub Organization needs both parentId and subOrgId
      this.navigateToEditSubOrganization(tab);
    } else {
      // Other tabs need orgId
      this.navigateWithOrgId(tab);
    }
  }

  private navigateWithOrgId(tab: any) {
    // Use userTopOrgId from login response as primary source
    let orgId: string | null = this.authService.userTopOrgId;

    // For users with AccessProductSupportConsole, check if they have selected a different org
    if (this.authService.hasPermission('AccessProductSupportConsole')) {
      this.orgState.orgResponse$
        .subscribe((org) => {
          const selectedOrgId = org?.orgId || org?.id || null;
          // Use selected org if available, otherwise fall back to userTopOrgId
          orgId = selectedOrgId || this.authService.userTopOrgId;

          if (orgId) {
            this.router.navigate([tab.route, orgId], {
              relativeTo: this.route,
            });
          } else {
            this.handleNoOrgIdError();
          }
        })
        .unsubscribe();
    } else if (orgId) {
      // For non-AccessProductSupportConsole users, use userTopOrgId directly
      this.router.navigate([tab.route, orgId], { relativeTo: this.route });
    } else {
      this.handleNoOrgIdError();
    }
  }

  private navigateToEditSubOrganization(tab: any) {
    // Navigate to show-sub-organization to display the table of sub-organizations
    // Use userTopOrgId from login response as primary source
    const orgId: string | null = this.authService.userTopOrgId;

    if (this.authService.hasPermission('AccessProductSupportConsole')) {
      this.handleSupportConsoleNavigation(orgId);
    } else {
      this.handleStandardUserNavigation(orgId);
    }
  }

  private handleSupportConsoleNavigation(defaultOrgId: string | null) {
    this.orgState.orgResponse$
      .subscribe((org) => {
        const selectedOrgId = this.getSelectedOrgId(org);
        const finalOrgId = selectedOrgId || defaultOrgId;
        this.navigateToSubOrganization(finalOrgId);
      })
      .unsubscribe();
  }

  private handleStandardUserNavigation(orgId: string | null) {
    this.navigateToSubOrganization(orgId);
  }

  private getSelectedOrgId(org: any): string | null {
    return org?.orgId || org?.id || null;
  }

  private navigateToSubOrganization(orgId: string | null) {
    if (orgId) {
      this.router.navigate(['show-sub-organization', orgId], {
        relativeTo: this.route,
      });
    } else {
      this.handleNoOrgIdError();
    }
  }

  private handleNoOrgIdError() {
    console.warn(
      'No orgId available for navigation. Please create a customer first.'
    );
    this.router.navigate(['add-customer'], { relativeTo: this.route });
  }
}